import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

// Encapsulates plan/offer loading, Stripe checkout redirects, and post-checkout polling.
export function useBilling(currentOrgId, refreshOrgs) {
  const [plans, setPlans] = useState([]);
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/offer").then((r) => setOffer(r.data)).catch(() => {});
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const offerActive = offer?.active && offer?.plan;
  const offerCountdown = (() => {
    if (!offer?.ends_at) return null;
    const ms = new Date(offer.ends_at).getTime() - Date.now();
    if (ms <= 0) return null;
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    const p = (n) => String(n).padStart(2, "0");
    return (d > 0 ? `${d}d ` : "") + `${p(h)}:${p(m)}:${p(sec)}`;
  })();

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 6;
    if (attempts >= maxAttempts) {
      setPolling(false);
      toast.error("Payment status check timed out. Refresh to see your plan.");
      return;
    }
    try {
      const res = await api.get(`/billing/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPolling(false);
        await refreshOrgs();
        toast.success("Payment successful! Your plan is now active.");
        return;
      }
      if (res.data.status === "expired") {
        setPolling(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPolling(false);
      toast.error("Could not verify payment.");
    }
  }, [refreshOrgs]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      setPolling(true);
      window.history.replaceState({}, "", "/billing");
      pollStatus(sessionId);
    }
  }, [pollStatus]);

  const claimLifetime = async () => {
    setBusy("lifetime");
    try {
      const res = await api.post(`/orgs/${currentOrgId}/billing/lifetime-checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const upgrade = async (planKey) => {
    setBusy(planKey);
    try {
      const res = await api.post(`/orgs/${currentOrgId}/billing/checkout`, { plan: planKey, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const manageBilling = async () => {
    setBusy("portal");
    try {
      const res = await api.post(`/orgs/${currentOrgId}/billing/portal`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    }
  };

  return { plans, offer, busy, polling, offerActive, offerCountdown, claimLifetime, upgrade, manageBilling };
}
